// Where E2EE hooks into the MoQ pipeline, without patching @moq/*:
//
// Publish: the encoders get their MoQ track from Broadcast.video()/audio()
// (a Rendition whose `track` is the demand-gated Track.Producer). Our
// Broadcast subclass hands them a Rendition whose producer encrypts every
// frame the encoder writes (the WebCodecs chunk inside hang's legacy
// container) before it reaches MoQ.
//
// Watch: the decoders get their MoQ broadcast from
// Broadcast.relativeBroadcast() and read groups/frames from it. Our
// Broadcast subclass returns a consumer whose groups decrypt every frame
// before the container parser and the WebCodecs decoder see it.
//
// Catalog, @moq/room metadata and the data track do not go through these
// paths and stay readable for the relay (see docs/strands/e2ee.md).

import type * as Moq from '@moq/net';
import * as Publish from '@moq/publish';
import {Effect, Signal} from '@moq/signals';
import * as Watch from '@moq/watch';
import {frameAAD, type MediaCrypto} from './e2ee';

type Frame = Moq.Group.Frame;
type GroupProducer = Moq.Group.Producer;
type GroupConsumer = Moq.Group.Consumer;

/** Forwards everything to target (methods bound to it) except the overrides. */
function proxy<T extends object>(target: T, overrides: Partial<Record<PropertyKey, unknown>>): T {
	return new Proxy(target, {
		get(t, prop) {
			if (Object.hasOwn(overrides, prop)) return overrides[prop as keyof typeof overrides];
			const value = Reflect.get(t, prop, t);
			return typeof value === 'function' ? value.bind(t) : value;
		},
	});
}

/**
 * A Track.Producer whose frames are encrypted. Encryption is async, so
 * writes and group closes go through one queue per track and keep their
 * order; groups are appended right away (their sequence stays in order).
 */
function sealingProducer(track: Moq.Track.Producer, crypto: MediaCrypto, aad: Uint8Array): Moq.Track.Producer {
	let queue: Promise<void> = Promise.resolve();
	const enqueue = (sealed: Promise<Uint8Array | undefined> | undefined, apply: (payload?: Uint8Array) => void) => {
		queue = queue
			.then(async () => apply(sealed ? await sealed : undefined))
			.catch((error: unknown) => console.warn('[parley/rtc] e2ee write failed', error));
	};
	const writeTo = (group: GroupProducer, frame: Frame) => {
		enqueue(crypto.seal(frame.payload, aad), (payload) => {
			if (payload && !group.isClosed) group.writeFrame({...frame, payload});
		});
	};
	const wrapGroup = (group: GroupProducer): GroupProducer =>
		proxy(group, {
			writeFrame: (frame: Frame) => writeTo(group, frame),
			close: (abort?: Error) => enqueue(undefined, () => group.close(abort)),
		});
	return proxy(track, {
		appendGroup: () => wrapGroup(track.appendGroup()),
		writeFrame: (frame: Frame) => {
			const group = track.appendGroup();
			writeTo(group, frame);
			enqueue(undefined, () => group.close());
		},
	});
}

/** A Broadcast (publish) whose audio/video renditions are end-to-end encrypted. */
export class SealedBroadcast extends Publish.Broadcast {
	#crypto: MediaCrypto;
	#identity: string;
	#kind: string;
	#effects: Array<Effect> = [];

	constructor(
		props: ConstructorParameters<typeof Publish.Broadcast>[0],
		crypto: MediaCrypto,
		identity: string,
		kind: string,
	) {
		super(props);
		this.#crypto = crypto;
		this.#identity = identity;
		this.#kind = kind;
	}

	override video(name: string): ReturnType<Publish.Broadcast['video']> {
		return this.#seal(super.video(name));
	}

	override audio(name: string): ReturnType<Publish.Broadcast['audio']> {
		return this.#seal(super.audio(name));
	}

	#seal<C>(rendition: Publish.Rendition<C>): Publish.Rendition<C> {
		const aad = frameAAD(this.#identity, this.#kind, rendition.name);
		const track = new Signal<Moq.Track.Producer | undefined>(undefined);
		const effect = new Effect();
		this.#effects.push(effect);
		effect.run((e) => {
			const raw = e.get(rendition.track);
			track.set(raw ? sealingProducer(raw, this.#crypto, aad) : undefined);
		});
		// The encoder writes `config` (catalog entry) on the real rendition and
		// reads `track` from ours.
		const sealed = {
			name: rendition.name,
			kind: rendition.kind,
			config: rendition.config,
			track,
			close: () => {
				effect.close();
				this.#effects = this.#effects.filter((x) => x !== effect);
				rendition.close();
			},
		};
		return sealed as unknown as Publish.Rendition<C>;
	}

	override close(): void {
		for (const effect of this.#effects) effect.close();
		this.#effects = [];
		super.close();
	}
}

/** A Group.Consumer whose frames are decrypted; a frame that cannot be decrypted ends the group. */
function openingGroup(group: GroupConsumer, crypto: MediaCrypto, aad: Uint8Array): GroupConsumer {
	const open = async <F extends {payload: Uint8Array}>(frame: F | undefined): Promise<F | undefined> => {
		if (!frame) return frame;
		const payload = await crypto.open(frame.payload, aad);
		// Throwing truncates the group in hang's container consumer (like a
		// stream reset): the decoder resumes at the next keyframe.
		if (!payload) throw new Error('e2ee: frame cannot be decrypted');
		return {...frame, payload};
	};
	return proxy(group, {
		readFrame: async () => open(await group.readFrame()),
		readFrameSequence: async (options?: unknown) =>
			open(await group.readFrameSequence(options as Parameters<GroupConsumer['readFrameSequence']>[0])),
		tryReadFrame: () => {
			throw new Error('e2ee: synchronous reads are not supported on encrypted tracks');
		},
		tryReadFrameSequence: () => {
			throw new Error('e2ee: synchronous reads are not supported on encrypted tracks');
		},
	});
}

function openingSubscriber(sub: Moq.Track.Subscriber, crypto: MediaCrypto, aad: Uint8Array): Moq.Track.Subscriber {
	const wrap = (g: GroupConsumer | undefined) => (g ? openingGroup(g, crypto, aad) : g);
	return proxy(sub, {
		recvGroup: async () => wrap(await sub.recvGroup()),
		nextGroup: async () => wrap(await sub.nextGroup()),
		tryRecvGroup: () => wrap(sub.tryRecvGroup()),
		fork: (options?: Parameters<Moq.Track.Subscriber['fork']>[0]) => openingSubscriber(sub.fork(options), crypto, aad),
		readFrame: () => {
			throw new Error('e2ee: track-level reads are not supported on encrypted tracks');
		},
	});
}

/** A Broadcast (watch) whose media tracks are decrypted before decoding. */
export class OpenedBroadcast extends Watch.Broadcast {
	#crypto: MediaCrypto;
	#identity: string;
	#kind: string;
	#wrapped = new WeakMap<Moq.Broadcast.Consumer, Moq.Broadcast.Consumer>();

	constructor(
		props: ConstructorParameters<typeof Watch.Broadcast>[0],
		crypto: MediaCrypto,
		identity: string,
		kind: string,
	) {
		super(props);
		this.#crypto = crypto;
		this.#identity = identity;
		this.#kind = kind;
	}

	override relativeBroadcast(
		effect: Parameters<Watch.Broadcast['relativeBroadcast']>[0],
		rel: string | undefined,
	): Moq.Broadcast.Consumer | undefined {
		const consumer = super.relativeBroadcast(effect, rel);
		if (!consumer) return consumer;
		let wrapped = this.#wrapped.get(consumer);
		if (!wrapped) {
			const subscribe = (name: string, sub: Moq.Track.Subscriber) =>
				openingSubscriber(sub, this.#crypto, frameAAD(this.#identity, this.#kind, name));
			wrapped = proxy(consumer, {
				track: (name: string) => {
					const track = consumer.track(name);
					return proxy(track, {
						subscribe: (options?: Parameters<typeof track.subscribe>[0]) => subscribe(name, track.subscribe(options)),
					});
				},
				subscribe: (name: string, options?: Parameters<Moq.Broadcast.Consumer['subscribe']>[1]) =>
					subscribe(name, consumer.subscribe(name, options)),
			});
			this.#wrapped.set(consumer, wrapped);
		}
		return wrapped;
	}
}
